import { useQuery } from "@tanstack/react-query";
import { env } from "@/env";
import { queryKeys } from "@/lib/query-keys";

export type BillingAccess = {
  isBillingManager: boolean;
  memberRole: string | null;
  organizationId: string;
  billingManagedByRole: string | null;
};

async function fetchBillingAccess(): Promise<BillingAccess> {
  const response = await fetch(`${env.VITE_API_URL}/api/organization/billing-access`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch billing access");
  }
  return (await response.json()) as BillingAccess;
}

export function useBillingAccess(enabled = true) {
  return useQuery({
    enabled,
    queryFn: fetchBillingAccess,
    queryKey: queryKeys.organizations.billingAccess(),
  });
}

/**
 * Returns `true` when the current user can manage billing for the active
 * organization (owner by default, or the role configured in
 * `organization.billingManagedByRole`). Mirrors
 * `hasOrganizationBillingManagerAccess` on the server (see ADR-0007).
 */
export function useIsBillingManager(): boolean {
  const { data, isPending } = useBillingAccess();
  if (isPending) return false;
  return data?.isBillingManager ?? false;
}
